import { type IAssignedAgent, type IAssignmentMeta } from '../../../interface/agent-assignment';
import { CopyValue } from '../../utils/copy';
import { useCopy } from '../../utils/hooks';
import { CheckSVG, CopySVG } from '../../utils/svgs';
import { UnreadPill } from '../../utils/unread-pill';
import { type CoverageState } from '../overview/quick-analytics/utils';
import { agentDisplayName, agentRoleLabel, avatarTint, formatPhoneForWa, formatUploadedAt, initials } from './utils';

// ────────────────────────────────────────────────────────────
// Inline glyphs (currentColor so they adapt to light/dark)
// ────────────────────────────────────────────────────────────
const PhoneIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M6.6 2.5 8 2.6c.5 0 .9.3 1 .8l.7 2.7c.1.4 0 .9-.4 1.2L7.7 8.6a13 13 0 0 0 5.7 5.7l1.3-1.6c.3-.4.8-.5 1.2-.4l2.7.7c.5.1.8.5.8 1l.1 1.4c0 1.3-1.1 2.4-2.4 2.3A15.5 15.5 0 0 1 4.2 4.9 2.3 2.3 0 0 1 6.6 2.5Z'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinejoin='round'
		/>
	</svg>
);
const MailIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<rect
			x='3'
			y='5'
			width='18'
			height='14'
			rx='2.5'
			stroke='currentColor'
			strokeWidth='1.6'
		/>
		<path
			d='m4 7 8 6 8-6'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);
const WhatsAppIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M12 3a9 9 0 0 0-7.7 13.6L3 21l4.5-1.2A9 9 0 1 0 12 3Z'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinejoin='round'
		/>
		<path
			d='M9 8.3c.2-.5.4-.5.6-.5h.5c.2 0 .4 0 .6.5l.6 1.4c0 .2 0 .4-.1.5l-.4.5c-.1.2-.2.3 0 .6.3.5.8 1.1 1.4 1.5.5.3.7.3.9.1l.5-.5c.2-.2.3-.2.5-.1l1.4.7c.2.1.3.2.3.4 0 .6-.4 1.2-.8 1.4-.5.2-1.2.3-3-.5-1.8-.8-3-2.6-3.1-2.8-.1-.2-.7-1-.7-1.9s.4-1.3.5-1.5Z'
			fill='currentColor'
		/>
	</svg>
);
const UserIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<circle
			cx='12'
			cy='8'
			r='3.5'
			stroke='currentColor'
			strokeWidth='1.6'
		/>
		<path
			d='M5 19a7 7 0 0 1 14 0'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinecap='round'
		/>
	</svg>
);
const WarnIcon = () => (
	<svg
		viewBox='0 0 20 20'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M10 6.7v3.6M10 13.4h.01M8.6 2.6 1.7 14.5c-.6 1 .1 2.3 1.4 2.3h13.8c1.3 0 2-1.3 1.4-2.3L11.4 2.6c-.6-1.1-2.2-1.1-2.8 0Z'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);
const MapPinIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M12 21s7-5.3 7-11a7 7 0 1 0-14 0c0 5.7 7 11 7 11Z'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinejoin='round'
		/>
		<circle
			cx='12'
			cy='10'
			r='2.5'
			stroke='currentColor'
			strokeWidth='1.6'
		/>
	</svg>
);

// ────────────────────────────────────────────────────────────
// Entity context strip
// ────────────────────────────────────────────────────────────
interface IEntityContextProps {
	level: string;
	code: string;
	analyticsChip?: { label: string; tone: 'success' | 'warning' | 'danger' } | null;
}

export const EntityContextHeader = ({ level, code, analyticsChip }: IEntityContextProps) => (
	<div className='child-agent__context'>
		<span
			className='child-agent__context-icon'
			aria-hidden='true'
		>
			<MapPinIcon />
		</span>
		<div className='child-agent__context-text'>
			<span className='child-agent__eyebrow'>{level}</span>
			<span className='child-agent__codeline'>
				<span className='child-agent__code'>{code}</span>
				<CopyValue
					value={code}
					label='code'
					size={14}
				/>
			</span>
		</div>
		{analyticsChip ? (
			<span className={`child-agent__result-chip child-agent__result-chip--${analyticsChip.tone}`}>{analyticsChip.label}</span>
		) : null}
	</div>
);

// ────────────────────────────────────────────────────────────
// Agent identity card
// ────────────────────────────────────────────────────────────
interface IIdentityProps {
	agent: IAssignedAgent;
	seed: string;
	tone: 'success' | 'neutral';
}

export const AgentIdentityCard = ({ agent, seed, tone }: IIdentityProps) => (
	<div
		className={`child-agent__identity child-agent__identity--${tone}`}
		role='group'
		aria-label='Assigned agent details'
	>
		<span
			className='child-agent__avatar'
			style={{ background: avatarTint(seed) }}
			aria-hidden='true'
		>
			{initials(agent.firstName, agent.lastName, agent.userName)}
		</span>
		<div className='child-agent__identity-text'>
			<p className='child-agent__name'>{agentDisplayName(agent)}</p>
			<div className='child-agent__identity-meta'>
				<span className='child-agent__role-chip'>{agentRoleLabel(agent)}</span>
				<span className={`child-agent__active ${agent.isActive ? 'is-active' : 'is-inactive'}`}>
					<span
						className='child-agent__active-dot'
						aria-hidden='true'
					/>
					{agent.isActive ? 'Active' : 'Inactive'}
				</span>
			</div>
		</div>
	</div>
);

// ────────────────────────────────────────────────────────────
// Contact actions
// ────────────────────────────────────────────────────────────
const ChatIcon = () => (
	<svg
		viewBox='0 0 20 20'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M3 5.5A2.5 2.5 0 0 1 5.5 3h9A2.5 2.5 0 0 1 17 5.5v6a2.5 2.5 0 0 1-2.5 2.5H8l-4 3v-3.2A2.5 2.5 0 0 1 3 11.5v-6Z'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinejoin='round'
		/>
	</svg>
);

export interface IContactMessageAction {
	onClick: () => void;
	unread?: number;
}

export const ContactActions = ({ agent, message }: { agent: IAssignedAgent; message?: IContactMessageAction }) => {
	const copyProps = useCopy();
	const name = agentDisplayName(agent);
	const phone = agent.phoneNumber?.trim();
	const email = agent.email?.trim();
	const wa = formatPhoneForWa(phone);

	const onCopy = (value: string, key: string) => {
		copyProps.setAction(key);
		copyProps.copy(value);
	};
	const copied = (key: string) => copyProps.copySuccess && copyProps.action === key;

	if (!phone && !email && !message) return null;

	return (
		<div className='child-agent__contacts'>
			{/* In-app first: it has a record, presence and read state. Call / WhatsApp / Email stay
			    one tap away as the off-platform fallbacks. */}
			{message ? (
				<button
					type='button'
					className='child-agent__action child-agent__action--message'
					onClick={message.onClick}
					aria-label={
						message.unread && message.unread > 0
							? `Message ${name} in the app, ${message.unread} unread`
							: `Message ${name} in the app`
					}
				>
					<span className='child-agent__action-icon'>
						<ChatIcon />
					</span>
					Message
					<UnreadPill
						count={message.unread ?? 0}
						className='child-agent__action-badge'
					/>
				</button>
			) : null}
			{phone ? (
				<>
					<a
						className='child-agent__action'
						href={`tel:${phone}`}
						aria-label={`Call ${name}`}
					>
						<span className='child-agent__action-icon'>
							<PhoneIcon />
						</span>
						Call
					</a>
					{wa ? (
						<a
							className='child-agent__action'
							href={`https://wa.me/${wa}`}
							target='_blank'
							rel='noopener noreferrer'
							aria-label={`Message ${name} on WhatsApp`}
						>
							<span className='child-agent__action-icon'>
								<WhatsAppIcon />
							</span>
							WhatsApp
						</a>
					) : null}
				</>
			) : null}
			{email ? (
				<a
					className='child-agent__action'
					href={`mailto:${email}`}
					aria-label={`Email ${name}`}
				>
					<span className='child-agent__action-icon'>
						<MailIcon />
					</span>
					Email
				</a>
			) : null}

			<div className='child-agent__copyrow'>
				{phone ? (
					<button
						type='button'
						className={`child-agent__copy ${copied('phone') ? 'is-done' : ''}`}
						onClick={() => onCopy(phone, 'phone')}
						aria-label={`Copy phone number for ${name}`}
					>
						<span className='child-agent__copy-icon'>
							{copied('phone') ? (
								<CheckSVG
									width='15'
									height='15'
									color='currentColor'
								/>
							) : (
								<CopySVG />
							)}
						</span>
						<span className='child-agent__copy-text'>
							<span className='child-agent__copy-label'>Phone</span>
							<span className='child-agent__copy-value'>{phone}</span>
						</span>
					</button>
				) : null}
				{email ? (
					<button
						type='button'
						className={`child-agent__copy ${copied('email') ? 'is-done' : ''}`}
						onClick={() => onCopy(email, 'email')}
						aria-label={`Copy email for ${name}`}
					>
						<span className='child-agent__copy-icon'>
							{copied('email') ? (
								<CheckSVG
									width='15'
									height='15'
									color='currentColor'
								/>
							) : (
								<CopySVG />
							)}
						</span>
						<span className='child-agent__copy-text'>
							<span className='child-agent__copy-label'>Email</span>
							<span className='child-agent__copy-value'>{email}</span>
						</span>
					</button>
				) : null}
			</div>
		</div>
	);
};

// ────────────────────────────────────────────────────────────
// Status block
// ────────────────────────────────────────────────────────────
const STATUS_COPY: Record<CoverageState, { tone: 'success' | 'warning' | 'danger' | 'neutral'; badge: string; line: string }> = {
	uploaded: { tone: 'success', badge: 'Result uploaded', line: 'This agent has submitted the result for this entity.' },
	pending: { tone: 'warning', badge: 'Awaiting upload', line: 'Assigned, but no result has been submitted yet.' },
	conflict: {
		tone: 'danger',
		badge: 'Disputed assignment',
		line: 'More than one agent has been linked to this entity. Showing the most recent — the coordination team is resolving this.',
	},
	agentUnavailable: {
		tone: 'warning',
		badge: 'Account unavailable',
		line: "This entity is assigned, but the agent's account record is missing.",
	},
	unassigned: { tone: 'neutral', badge: 'Unassigned', line: 'No agent is covering this entity yet.' },
};

export const StatusBlock = ({ state, meta }: { state: CoverageState; meta: IAssignmentMeta | null }) => {
	const copy = STATUS_COPY[state];
	const uploadedAt = state === 'uploaded' ? formatUploadedAt(meta?.dateUploaded) : null;
	return (
		<div className={`child-agent__status child-agent__status--${copy.tone}`}>
			<span className='child-agent__status-badge'>{copy.badge}</span>
			<p className='child-agent__status-line'>{copy.line}</p>
			{uploadedAt ? <p className='child-agent__status-date'>Uploaded {uploadedAt}</p> : null}
		</div>
	);
};

// ────────────────────────────────────────────────────────────
// State views
// ────────────────────────────────────────────────────────────
export const AgentSkeleton = () => (
	<div className='child-agent__skeleton f-column-20'>
		<div className='child-agent__sk-context skeleton' />
		<div className='child-agent__sk-card skeleton' />
		<div className='child-agent__sk-actions'>
			<div className='child-agent__sk-pill skeleton' />
			<div className='child-agent__sk-pill skeleton' />
			<div className='child-agent__sk-pill skeleton' />
		</div>
	</div>
);

export const AgentErrorState = ({ onRetry, forbidden }: { onRetry: () => void; forbidden?: boolean }) => (
	<div className='child-agent__state'>
		<span
			className='child-agent__state-icon child-agent__state-icon--danger'
			aria-hidden='true'
		>
			<WarnIcon />
		</span>
		<p className='child-agent__state-title'>
			{forbidden ? "You don't have access to this entity's agent." : 'We couldn’t load the assigned agent.'}
		</p>
		{!forbidden ? (
			<button
				type='button'
				className='child-agent__retry'
				onClick={onRetry}
			>
				Try again
			</button>
		) : null}
	</div>
);

export const CoverageGapState = ({ level }: { level: string }) => (
	<div className='child-agent__state'>
		<span
			className='child-agent__state-icon child-agent__state-icon--neutral'
			aria-hidden='true'
		>
			<UserIcon />
		</span>
		<p className='child-agent__state-title'>No agent assigned yet</p>
		<p className='child-agent__state-hint'>
			This {level.toLowerCase()} has no party agent covering it. Coverage gaps are visible to the coordination team.
		</p>
	</div>
);
